import { motion } from "framer-motion";
import { Zap } from "lucide-react";

const chapters = [
  {
    n: "01",
    title: "What we manufacture",
    copy: "Electromechanical switches and precision switching components — engineered and built to specification, under one roof.",
    img: "/assets/toolroom.jpg",
    alt: "Precision grinding on a tool-room machine at Devkii Electricals",
  },
  {
    n: "02",
    title: "How we build",
    copy: "Disciplined processes, careful tooling and rigorous inspection keep every batch consistent — switch after switch.",
    img: "/assets/precision.jpg",
    alt: "Precision measurement of a machined component",
  },
  {
    n: "03",
    title: "Who we build for",
    copy: "OEMs and established Indian brands that need a dependable, long-term manufacturing partner — not a vendor.",
    img: null,
  },
];

const reveal = {
  hidden: { opacity: 0, y: 48 },
  visible: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: d },
  }),
};

export const Chapters = () => (
  <section id="chapters" data-testid="chapters-section" className="bg-paper">
    <div className="mx-auto w-full max-w-[1400px] px-6 py-24 sm:px-10 lg:px-16 lg:py-36">
      <motion.div
        variants={reveal}
        custom={0}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        className="flex flex-col gap-8 border-b border-ink/10 pb-12 lg:flex-row lg:items-end lg:justify-between"
      >
        <div>
          <p className="mb-4 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-brand">
            <span className="inline-block h-2 w-2 bg-brand" />
            Capability
          </p>
          <h2 className="font-heading text-4xl font-black tracking-tighter sm:text-5xl lg:text-6xl">
            Built with intent.
          </h2>
        </div>
        <p className="max-w-xs text-sm leading-relaxed text-ink/50">
          A short introduction to how Devkii Electricals works — the detail lives
          on the pages that follow.
        </p>
      </motion.div>

      {chapters.map((c, i) => (
        <motion.div
          key={c.n}
          variants={reveal}
          custom={0.1}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          data-testid={`chapter-${c.n}`}
          className="grid items-center gap-10 border-b border-ink/10 py-14 lg:grid-cols-12 lg:gap-12 lg:py-20"
        >
          <span
            aria-hidden
            className="select-none font-heading text-[6rem] font-black leading-none lg:col-span-3 lg:text-[10rem]"
            style={{ WebkitTextStroke: "1.5px rgba(10,10,10,0.8)", color: "transparent" }}
          >
            {c.n}
          </span>
          <div className="lg:col-span-5">
            <h3 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              {c.title}
            </h3>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink/60">{c.copy}</p>
          </div>
          <div className="lg:col-span-4">
            {c.img ? (
              <div className="group aspect-[4/3] overflow-hidden bg-ink">
                <img
                  src={c.img}
                  alt={c.alt}
                  className="h-full w-full object-cover grayscale transition-[transform,filter] duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"
                />
              </div>
            ) : (
              <div className="flex aspect-[4/3] flex-col items-start justify-between bg-brand p-8 text-white">
                <Zap size={36} className="fill-white text-white" />
                <div>
                  <p className="font-heading text-2xl font-bold tracking-tight">Known for Innovations</p>
                  <p className="mt-2 text-sm text-white/70">The promise behind every component we ship.</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);
